import { useState, useRef } from "react";
import { useToast } from "@/hooks/use-toast";

const TEAL = "#6FAFB5";
const NAVY = "#1F2E3B";
const CREAM = "#F7F4EE";
const MUTED = "#536270";

function Field({ label, name, value, onChange, type = "text", optional }: {
  label: string; name: string; value: string; onChange: (v: string) => void;
  type?: "text" | "textarea"; optional?: boolean;
}) {
  const base = "w-full rounded-xl px-4 py-3 text-sm outline-none border transition-all";
  const style = { borderColor: "#d6cfc5", background: "#fff", color: NAVY, fontFamily: "DM Sans, sans-serif" };
  return (
    <div className="flex flex-col gap-1.5">
      <label className="text-xs font-medium" style={{ color: NAVY }}>
        {label}
        {optional && <span className="ml-1 font-normal" style={{ color: MUTED }}>(optional)</span>}
      </label>
      {type === "textarea" ? (
        <textarea
          rows={3}
          value={value}
          onChange={e => onChange(e.target.value)}
          className={`${base} resize-vertical`}
          style={{ ...style, minHeight: 80 }}
          onFocus={e => (e.target.style.borderColor = TEAL)}
          onBlur={e => (e.target.style.borderColor = "#d6cfc5")}
        />
      ) : (
        <input
          type="text"
          value={value}
          onChange={e => onChange(e.target.value)}
          className={base}
          style={style}
          onFocus={e => (e.target.style.borderColor = TEAL)}
          onBlur={e => (e.target.style.borderColor = "#d6cfc5")}
        />
      )}
    </div>
  );
}

function parseOutput(text: string) {
  // Split on lines that look like section headings (all-caps or ##)
  const lines = text.split("\n");
  const result: Array<{ heading: boolean; text: string }> = [];
  for (const line of lines) {
    const trimmed = line.trim();
    if (!trimmed) { result.push({ heading: false, text: "" }); continue; }
    if (
      trimmed.startsWith("##") ||
      /^[A-Z][A-Z\s:&/]{5,}$/.test(trimmed) ||
      /^\*\*[^*]+\*\*$/.test(trimmed)
    ) {
      result.push({ heading: true, text: trimmed.replace(/^#+\s*/, "").replace(/\*\*/g, "") });
    } else {
      result.push({ heading: false, text: trimmed });
    }
  }
  return result;
}

export default function AdminResearch() {
  const { toast } = useToast();
  const [password, setPassword] = useState("");
  const [authed, setAuthed] = useState(false);
  const [authError, setAuthError] = useState(false);

  // Form fields
  const [clientName, setClientName] = useState("");
  const [businessName, setBusinessName] = useState("");
  const [businessType, setBusinessType] = useState("");
  const [location, setLocation] = useState("");
  const [websiteUrl, setWebsiteUrl] = useState("");
  const [facebookUrl, setFacebookUrl] = useState("");
  const [foundOnline, setFoundOnline] = useState("");
  const [problems, setProblems] = useState("");
  const [wins, setWins] = useState("");
  const [contactPref, setContactPref] = useState("");
  const [howHeard, setHowHeard] = useState("");
  const [notes, setNotes] = useState("");

  const [loading, setLoading] = useState(false);
  const [output, setOutput] = useState<string | null>(null);
  const outputRef = useRef<HTMLDivElement>(null);

  function handleAuth(e: React.FormEvent) {
    e.preventDefault();
    fetch("/api/admin/auth", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ password }),
    })
      .then(r => r.json())
      .then(d => {
        if (d.ok) { setAuthed(true); setAuthError(false); }
        else { setAuthError(true); }
      })
      .catch(() => setAuthError(true));
  }

  async function handleResearch(e: React.FormEvent) {
    e.preventDefault();
    if (!businessName) return;
    setLoading(true);
    setOutput(null);
    try {
      const res = await fetch("/api/admin/research", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          password,
          clientName, businessName, businessType, location,
          websiteUrl, facebookUrl, foundOnline, problems,
          wins, contactPref, howHeard, notes,
        }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Research failed");
      setOutput(data.brief);
    } catch (err: any) {
      toast({ title: "Research failed", description: err.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  }

  function copyFull() {
    if (!output) return;
    navigator.clipboard.writeText(output);
    toast({ title: "Copied", description: "Full brief copied to clipboard." });
  }

  function copyEmail() {
    if (!output) return;
    const emailMatch = output.match(/PROPOSAL EMAIL[\s\S]*/i);
    if (emailMatch) {
      navigator.clipboard.writeText(emailMatch[0].replace(/^PROPOSAL EMAIL[:\s]*/i, "").trim());
      toast({ title: "Copied", description: "Proposal email copied to clipboard." });
    } else {
      navigator.clipboard.writeText(output);
      toast({ title: "Copied", description: "Brief copied (proposal section not found separately)." });
    }
  }

  // ── Password gate ──
  if (!authed) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: CREAM }}>
        <div className="w-full max-w-sm p-8 rounded-2xl" style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
          <div className="flex items-center gap-2 mb-6">
            <svg width="24" height="14" viewBox="0 0 28 16" fill="none">
              <path d="M1 8 L5.5 8 L8 1.5 L10.5 14.5 L13.5 8 L27 8" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.3rem", color: NAVY }}>
              Biz<em style={{ fontStyle: "italic", color: "#3a7178" }}>Dr</em>
            </span>
            <span className="ml-2 text-xs font-medium" style={{ color: MUTED }}>Admin</span>
          </div>
          <h1 className="text-base font-semibold mb-5" style={{ color: NAVY }}>Research Tool — Private Access</h1>
          <form onSubmit={handleAuth} className="flex flex-col gap-4">
            <input
              type="password"
              placeholder="Password"
              value={password}
              onChange={e => setPassword(e.target.value)}
              autoFocus
              className="w-full rounded-xl px-4 py-3 text-sm outline-none border"
              style={{ borderColor: authError ? "#e05" : "#d6cfc5", background: CREAM, color: NAVY }}
            />
            {authError && <p className="text-xs" style={{ color: "#c0392b" }}>Incorrect password.</p>}
            <button type="submit" className="rounded-full py-3 text-sm font-medium"
              style={{ background: NAVY, color: "#fff" }}>
              Enter
            </button>
          </form>
        </div>
      </div>
    );
  }

  // ── Main page ──
  return (
    <div className="min-h-screen" style={{ background: CREAM, fontFamily: "'DM Sans', sans-serif", color: NAVY }}>
      {/* Header */}
      <header style={{ background: "rgba(247,244,238,.95)", backdropFilter: "blur(14px)", borderBottom: "1px solid #e4ddd2", position: "sticky", top: 0, zIndex: 50 }}>
        <div className="mx-auto max-w-7xl px-6 py-3 flex items-center gap-3">
          <svg width="24" height="14" viewBox="0 0 28 16" fill="none">
            <path d="M1 8 L5.5 8 L8 1.5 L10.5 14.5 L13.5 8 L27 8" stroke={TEAL} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <span style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.3rem", color: NAVY }}>
            Biz<em style={{ fontStyle: "italic", color: "#3a7178" }}>Dr</em>
          </span>
          <span className="text-xs px-2 py-1 rounded-full ml-1 font-medium" style={{ background: "#e4ddd2", color: MUTED }}>
            Research Tool
          </span>
        </div>
      </header>

      <main className="mx-auto max-w-7xl px-6 py-10">
        <div className="mb-8">
          <h1 style={{ fontFamily: "'Playfair Display', serif", fontSize: "1.8rem", fontWeight: 600, color: NAVY, marginBottom: 4 }}>
            New BizDr Research
          </h1>
          <p style={{ color: MUTED, fontSize: "0.9rem" }}>Fill in the client details from their BizDr Check and run the research brief.</p>
        </div>

        <div className="grid lg:grid-cols-2 gap-8 items-start">

          {/* ── LEFT: Input panel ── */}
          <form onSubmit={handleResearch} className="flex flex-col gap-4 rounded-2xl p-7" style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
            <h2 className="font-semibold text-base mb-2" style={{ color: NAVY }}>Client Details</h2>
            <Field label="Client name" name="clientName" value={clientName} onChange={setClientName} />
            <Field label="Business name" name="businessName" value={businessName} onChange={setBusinessName} />
            <Field label="Business type" name="businessType" value={businessType} onChange={setBusinessType} />
            <Field label="Location (town and state)" name="location" value={location} onChange={setLocation} />
            <Field label="Website URL" name="websiteUrl" value={websiteUrl} onChange={setWebsiteUrl} optional />
            <Field label="Facebook URL" name="facebookUrl" value={facebookUrl} onChange={setFacebookUrl} optional />
            <Field label="Where they're currently found online" name="foundOnline" value={foundOnline} onChange={setFoundOnline} />
            <Field label="Problems they ticked" name="problems" value={problems} onChange={setProblems} type="textarea" />
            <Field label="Wins they want" name="wins" value={wins} onChange={setWins} type="textarea" />
            <Field label="Contact preference" name="contactPref" value={contactPref} onChange={setContactPref} />
            <Field label="How they heard about BizDr" name="howHeard" value={howHeard} onChange={setHowHeard} />
            <Field label="Any other notes" name="notes" value={notes} onChange={setNotes} type="textarea" optional />

            <button
              type="submit"
              disabled={loading || !businessName}
              className="mt-2 w-full rounded-full py-4 text-base font-semibold transition-all hover:-translate-y-0.5 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ background: TEAL, color: "#fff" }}>
              {loading ? `Researching ${businessName}…` : "Run BizDr Research"}
            </button>
          </form>

          {/* ── RIGHT: Output panel ── */}
          <div className="rounded-2xl p-7 min-h-96" style={{ background: "#fff", border: "1px solid #e4ddd2" }}>
            <div className="flex items-center justify-between mb-5 flex-wrap gap-3">
              <h2 className="font-semibold text-base" style={{ color: NAVY }}>Research Brief</h2>
              {output && (
                <div className="flex gap-2">
                  <button onClick={copyFull}
                    className="rounded-full px-4 py-2 text-xs font-medium transition-all hover:-translate-y-0.5"
                    style={{ background: TEAL, color: "#fff" }}>
                    Copy Full Brief
                  </button>
                  <button onClick={copyEmail}
                    className="rounded-full px-4 py-2 text-xs font-medium transition-all hover:-translate-y-0.5"
                    style={{ background: NAVY, color: "#fff" }}>
                    Copy Proposal Email
                  </button>
                </div>
              )}
            </div>

            {loading && (
              <div className="flex flex-col items-center justify-center py-20 gap-4">
                <div className="animate-spin rounded-full border-4 border-t-transparent"
                  style={{ width: 44, height: 44, borderColor: `${TEAL} transparent ${TEAL} ${TEAL}` }} />
                <p className="text-sm" style={{ color: MUTED }}>Researching {businessName}…</p>
                <p className="text-xs" style={{ color: MUTED }}>This may take 30–60 seconds</p>
              </div>
            )}

            {!loading && !output && (
              <div className="flex flex-col items-center justify-center py-20 gap-3">
                <div style={{ fontSize: "2.5rem" }}>🔍</div>
                <p className="text-sm" style={{ color: MUTED }}>Fill in the client details and click Run BizDr Research.</p>
              </div>
            )}

            {!loading && output && (
              <div ref={outputRef} className="text-sm leading-relaxed" style={{ color: NAVY }}>
                {parseOutput(output).map((block, i) => {
                  if (!block.text) return <div key={i} className="h-3" />;
                  if (block.heading) {
                    return (
                      <h3 key={i} className="font-semibold text-sm uppercase tracking-wide mt-6 mb-2 pb-1"
                        style={{ color: TEAL, borderBottom: `1px solid #e4ddd2`, letterSpacing: "0.08em" }}>
                        {block.text}
                      </h3>
                    );
                  }
                  const text = block.text.replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>").replace(/\*(.*?)\*/g, "<em>$1</em>");
                  return <p key={i} className="mb-1" style={{ lineHeight: 1.75 }} dangerouslySetInnerHTML={{ __html: text }} />;
                })}
              </div>
            )}
          </div>

        </div>
      </main>
    </div>
  );
}
